import { useRef, useState, useCallback } from "react";
import { DashboardOutlined } from "@ant-design/icons";

const ITEM_HEIGHT = 85; // consistent row height
const VIEWPORT_HEIGHT = 400;

const VirtualMeters = ({
  heading,
  meters,
  selectedMeters,
  meterColorMap,
  handleMeterSelection,
}) => {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);

  const onScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const totalHeight = meters.length * ITEM_HEIGHT;

  const startIndex = Math.floor(scrollTop / ITEM_HEIGHT);
  const visibleCount = Math.ceil(VIEWPORT_HEIGHT / ITEM_HEIGHT) + 3;
  const endIndex = Math.min(meters.length - 1, startIndex + visibleCount);

  const visibleMeters = meters.slice(startIndex, endIndex + 1);

  const getMeterKey = (meter) =>
    `${meter.meter_id},${(meter.register_number || "").trim()}`;

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      // Remove 'water-accountList' if it has conflicting height styles in CSS
      style={{
        maxHeight: "400px", // Use a fixed viewport height for the virtual window
        overflowY: "auto",
        position: "relative", // Crucial for absolute children
        paddingRight: "8px",
      }}
    >
      {/* This div creates the scrollable area */}
      <div style={{ height: totalHeight, position: "relative", width: "100%" }}>
        {visibleMeters.map((meter, i) => {
          const index = startIndex + i;
          const isSelected = selectedMeters.some(
            (item) =>
              item.code === heading.code &&
              item.meters.includes(getMeterKey(meter)),
          );

          const meterColor = meterColorMap[meter.meter_id] || "#bfbfbf";

          return (
            <div
              key={`${meter.meter_id}-${index}`} // Add index to key to force redraw on filter
              style={{
                position: "absolute",
                top: index * ITEM_HEIGHT,
                left: 0,
                right: 0,
                height: ITEM_HEIGHT,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px",
                borderRadius: "6px",
                cursor: "pointer",
                backgroundColor: isSelected ? "#e6f7ff" : "transparent",
                boxSizing: "border-box",
                borderBottom: "1px solid #f0f0f0", // Helps visual separation
              }}
              onClick={() =>
                handleMeterSelection(
                  heading.code,
                  meter.meter_id,
                  meter.register_number,
                )
              }
            >
              <DashboardOutlined
                style={{
                  fontSize: "24px",
                  color: isSelected ? meterColor : "#bfbfbf",
                  flexShrink: 0,
                }}
              />

              <div
                className="meter-details"
                style={{ lineHeight: 1.2, overflow: "hidden" }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px", // Reduced slightly to fit ITEM_HEIGHT better
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {meter.overided_name || "Unknown Meter"}
                </p>

                <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
                  {meter.meter_id}
                  {meter.register_number?.trim()
                    ? ` (${meter.register_number})`
                    : ""}
                </p>

                <div style={{ fontSize: "12px", marginTop: 2, color: "#888" }}>
                  Flow: {meter.flow.toLocaleString()} gallons
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualMeters;
